'use strict';

/**
 * Client-safe helpers for free-first / launch billing pause.
 * Prefer server-provided `freeFirst` from /api/subscription/pricing|status or user session.
 *
 * Cache TTLs and baseline Free limits are shared so web SPA, mobile, and Next marketing
 * cannot silently diverge on the same flag / fallback numerals.
 */

export const DEFAULT_MESSAGING = Object.freeze({
  bannerTitle: 'Launch access',
  bannerBody:
    'FuelCyclePro is free during launch with raised free limits. Pro plans and paid checkout will return later.',
  paywallTitle: 'Launch limit reached',
  paywallBody:
    'You have hit a temporary launch limit. Paid Pro checkout is paused for now - limits reset on the usual schedule, or contact support if you need help.',
  pricingCta: 'Coming soon',
  pricingNote:
    'Paid Pro checkout is paused during launch. Enjoy the expanded free tier while it lasts.',
  featureProLater:
    'This stays a Pro feature for now. Core training and fueling stay free during launch.',
});

/** Successful /api/subscription/pricing fetch TTL (ms). */
export const FREE_FIRST_CACHE_OK_MS = 60_000;
/** Failed fetch TTL - retry soon so a brief outage does not pin "mode off". */
export const FREE_FIRST_CACHE_ERR_MS = 5_000;

/**
 * Baseline FREE-tier display/enforcement numerals (must match server/constants/quotas.js FREE_*).
 * Used only as client fallback before pricing/status hydrates.
 */
export const BASELINE_FREE_LIMITS = Object.freeze({
  aiGenerations: 5,
  exports: 5,
  plans: 4,
  coachMessages: 3,
  mealPlansRolling30d: 6,
  mobilityPrescriptions: 2,
  nutritionSuggestionsShown: 4,
  raceDayReadyPerMonth: 0,
});

/**
 * Paid Pro generation floors (must match server/constants/quotas.js PRO_*).
 * Display and enforcement use resolveProGenerationLimits so launch Free caps
 * never outrank Pro on the same generation rows.
 */
export const BASELINE_PRO_LIMITS = Object.freeze({
  aiGenerations: 10,
  mealPlansRolling30d: 10,
  /** Paid Pro export is unlimited; floor vs launch Free so Pro never undercuts 20. */
  exports: Number.POSITIVE_INFINITY,
});

/**
 * Pro custom-plan / meal-prep / export caps: never below the live Free (launch) caps.
 * @param {unknown} freeLimits
 * @returns {{ aiGenerations: number, mealPlansRolling30d: number, exports: number }}
 */
export function resolveProGenerationLimits(freeLimits) {
  const src =
    freeLimits && typeof freeLimits === 'object' ? freeLimits : BASELINE_FREE_LIMITS;
  const ai = Number(/** @type {Record<string, unknown>} */ (src).aiGenerations);
  const meal = Number(
    /** @type {Record<string, unknown>} */ (src).mealPlansRolling30d,
  );
  const exp = Number(/** @type {Record<string, unknown>} */ (src).exports);
  return {
    aiGenerations: Math.max(
      BASELINE_PRO_LIMITS.aiGenerations,
      Number.isFinite(ai) && ai > 0 ? ai : BASELINE_FREE_LIMITS.aiGenerations,
    ),
    mealPlansRolling30d: Math.max(
      BASELINE_PRO_LIMITS.mealPlansRolling30d,
      Number.isFinite(meal) && meal > 0
        ? meal
        : BASELINE_FREE_LIMITS.mealPlansRolling30d,
    ),
    exports: Math.max(
      BASELINE_PRO_LIMITS.exports,
      Number.isFinite(exp) && exp > 0 ? exp : BASELINE_FREE_LIMITS.exports,
    ),
  };
}

/**
 * @param {unknown} freeFirst
 * @returns {{
 *   enabled: boolean,
 *   billingCheckoutDisabled: boolean,
 *   showBanner: boolean,
 *   unlockProSurfaces: boolean,
 *   quotaProfile: string,
 *   messaging: typeof DEFAULT_MESSAGING,
 * }}
 */
export function normalizeFreeFirstConfig(freeFirst) {
  if (!freeFirst || typeof freeFirst !== 'object') {
    return {
      enabled: false,
      billingCheckoutDisabled: false,
      showBanner: false,
      unlockProSurfaces: false,
      quotaProfile: 'off',
      messaging: { ...DEFAULT_MESSAGING },
    };
  }
  const messaging =
    freeFirst.messaging && typeof freeFirst.messaging === 'object'
      ? { ...DEFAULT_MESSAGING, ...freeFirst.messaging }
      : { ...DEFAULT_MESSAGING };
  return {
    enabled: !!freeFirst.enabled,
    billingCheckoutDisabled: !!freeFirst.billingCheckoutDisabled,
    showBanner: !!freeFirst.showBanner,
    unlockProSurfaces: !!freeFirst.unlockProSurfaces,
    quotaProfile: String(freeFirst.quotaProfile || 'off'),
    messaging,
  };
}

export function isCheckoutAvailable(freeFirst) {
  const cfg = normalizeFreeFirstConfig(freeFirst);
  return !cfg.billingCheckoutDisabled;
}

/**
 * Stripe Customer Portal / App Store Manage subscription is a live-billing control.
 * Hide while FREE_FIRST launch access is on or billing checkout is paused.
 */
export function shouldShowManageSubscription(freeFirst) {
  const cfg = normalizeFreeFirstConfig(freeFirst);
  return !cfg.billingCheckoutDisabled && !cfg.enabled;
}

/**
 * @param {unknown} raw
 * @returns {{
 *   aiGenerations: number,
 *   exports: number,
 *   plans: number,
 *   coachMessages: number,
 *   mealPlansRolling30d: number,
 *   mobilityPrescriptions?: number,
 * } | null}
 */
export function normalizeFreeLimits(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  const ai = Number(o.aiGenerations);
  const exports = Number(o.exports);
  const plans = Number(o.plans);
  const coach = Number(o.coachMessages);
  if (![ai, exports, plans, coach].every((n) => Number.isFinite(n) && n > 0)) {
    return null;
  }
  const meal = Number(o.mealPlansRolling30d);
  const mobility = Number(o.mobilityPrescriptions);
  return {
    aiGenerations: ai,
    exports,
    plans,
    coachMessages: coach,
    mealPlansRolling30d:
      Number.isFinite(meal) && meal > 0
        ? meal
        : BASELINE_FREE_LIMITS.mealPlansRolling30d,
    mobilityPrescriptions:
      Number.isFinite(mobility) && mobility > 0
        ? mobility
        : BASELINE_FREE_LIMITS.mobilityPrescriptions,
  };
}

/**
 * Per-runtime TTL cache (one instance per SPA/mobile/Next bundle).
 * @param {{ okMs?: number, errMs?: number }} [opts]
 */
export function createFreeFirstTtlCache(opts = {}) {
  const okMs = opts.okMs ?? FREE_FIRST_CACHE_OK_MS;
  const errMs = opts.errMs ?? FREE_FIRST_CACHE_ERR_MS;
  let value = null;
  let at = 0;
  let lastOk = false;
  /** @type {Promise<unknown>|null} */
  let inflight = null;

  return {
    /**
     * @param {number} [now]
     * @returns {{ hit: boolean, value: unknown }}
     */
    get(now = Date.now()) {
      if (value == null) return { hit: false, value: null };
      const ttl = lastOk ? okMs : errMs;
      if (now - at < ttl) return { hit: true, value };
      return { hit: false, value: null };
    },
    /**
     * @param {unknown} next
     * @param {number} [now]
     */
    setOk(next, now = Date.now()) {
      value = next;
      at = now;
      lastOk = true;
    },
    /**
     * @param {unknown} next
     * @param {number} [now]
     */
    setErr(next, now = Date.now()) {
      value = next;
      at = now;
      lastOk = false;
    },
    clear() {
      value = null;
      at = 0;
      lastOk = false;
      inflight = null;
    },
    /** @returns {Promise<unknown>|null} */
    getInflight() {
      return inflight;
    },
    /**
     * @param {Promise<unknown>|null} p
     */
    setInflight(p) {
      inflight = p;
    },
    /** @returns {boolean} */
    get lastFetchOk() {
      return lastOk;
    },
  };
}

/**
 * @param {{
 *   fetchJson: () => Promise<any>,
 *   cache: ReturnType<typeof createFreeFirstTtlCache>,
 *   force?: boolean,
 * }} args
 */
export async function loadPricingFreeFirstBundle({ fetchJson, cache, force = false }) {
  if (!force) {
    const hit = cache.get();
    if (hit.hit) return hit.value;
    const pending = cache.getInflight();
    if (pending) return pending;
  }

  const run = (async () => {
    try {
      const json = await fetchJson();
      if (!json || typeof json !== 'object') {
        throw new Error('empty pricing payload');
      }
      const freeFirst = normalizeFreeFirstConfig(json.freeFirst);
      const freeLimits =
        normalizeFreeLimits(json.free?.limits) || { ...BASELINE_FREE_LIMITS };
      const bundle = { freeFirst, freeLimits, json };
      cache.setOk(bundle);
      return bundle;
    } catch {
      const bundle = {
        freeFirst: normalizeFreeFirstConfig(null),
        freeLimits: { ...BASELINE_FREE_LIMITS },
        json: null,
      };
      cache.setErr(bundle);
      return bundle;
    } finally {
      cache.setInflight(null);
    }
  })();

  if (!force) cache.setInflight(run);
  return run;
}

